import { TrackMatcher } from '../services/TrackMatcher.service';
import { iTrack } from '../types/ITrack';
import {
  AssetMetadata,
  AssetProvider,
  DEFAULT_MATCHER_CONFIG,
  MatchEvidence,
} from '../types/MatchTypes';
import { normalizeForSearch } from './trackTextNormalize';
import { toDurationMs } from './toDurationMs';

const DURATION_HARD_REJECT_MS = 10000;
/** Pre-11.12.0 cloud saves accepted TrackMatcher at 50%+; 11.12.0 raised this to 85%. */
const CLOUD_MATCH_MIN_CONFIDENCE = 50;

export function trackToAssetMetadata(
  track: iTrack,
  providerId?: string,
): AssetMetadata & { id: string } {
  const trackArtist = track.Artist || '';
  return {
    id: providerId || track.ID || '',
    title: track.Title || '',
    artist: trackArtist,
    artists: trackArtist.split(',').map((a) => a.trim()),
    duration: toDurationMs(track.duration as number | string | undefined),
    album: track.Album,
    albumArt: track.AlbumCover,
    isrc: track.isrc,
  };
}

function isrcOf(track: iTrack): string | undefined {
  return track.isrc?.trim().toUpperCase() || undefined;
}

function durationDeltaMs(
  source: iTrack,
  candidate: iTrack,
): number | undefined {
  const sourceMs = toDurationMs(source.duration as number | string | undefined);
  const candidateMs = toDurationMs(
    candidate.duration as number | string | undefined,
  );
  if (sourceMs == null || candidateMs == null) {
    return undefined;
  }
  return Math.abs(sourceMs - candidateMs);
}

function durationWithinLimit(source: iTrack, candidate: iTrack): boolean {
  const delta = durationDeltaMs(source, candidate);
  return delta == null || delta <= DURATION_HARD_REJECT_MS;
}

export type CloudMatchResult = {
  found: boolean;
  matchedTrack: iTrack | undefined;
  confidence: number;
  requiresReview: boolean;
  reason?: string;
};

function unmatchedReason(
  delta: number | undefined,
  confidence: number,
  versionConflict?: string,
): string {
  if (delta != null && delta > DURATION_HARD_REJECT_MS) {
    return 'Closest match has a different length';
  }
  if (versionConflict) {
    return 'Closest match looks like a different version';
  }
  if (confidence > 0) {
    return 'Closest match was not close enough';
  }
  return 'Not found';
}

/** Title overlap fallback when TrackMatcher scores low (e.g. sparse API metadata). */
export function titleSimilarity(a: string, b: string): number {
  const normA = normalizeForSearch(a);
  const normB = normalizeForSearch(b);
  if (!normA || !normB) return 0;
  if (normA === normB) return 100;

  const wordsA = normA.split(' ').filter((word) => word.length > 1);
  const wordsB = normB.split(' ').filter((word) => word.length > 1);
  if (!wordsA.length || !wordsB.length) return 0;

  const commonWords = wordsA.filter((word) => wordsB.includes(word));
  return Math.round(
    ((commonWords.length * 2) / (wordsA.length + wordsB.length)) * 100,
  );
}

function artistSimilarity(a: string, b: string): number {
  const normA = normalizeForSearch(a);
  const normB = normalizeForSearch(b);
  if (!normA || !normB) return 0;
  if (normA === normB) return 100;
  if (normA.includes(normB) || normB.includes(normA)) return 85;

  const wordsA = normA.split(' ').filter((word) => word.length > 1);
  const wordsB = normB.split(' ').filter((word) => word.length > 1);
  const commonWords = wordsA.filter((word) => wordsB.includes(word));
  return Math.round(
    ((commonWords.length * 2) / (wordsA.length + wordsB.length)) * 100,
  );
}

function isUnknownArtist(artist: string | undefined): boolean {
  const normalized = normalizeForSearch(artist || '');
  return (
    !normalized || normalized === 'unknown' || normalized === 'unknown artist'
  );
}

/** Real artists on both sides have to agree; an unknown one can't disagree. */
function artistsAgree(source: iTrack, candidate: iTrack): boolean {
  return (
    isUnknownArtist(source.Artist) ||
    isUnknownArtist(candidate.Artist) ||
    artistSimilarity(source.Artist || '', candidate.Artist || '') >= 50
  );
}

function passesCloudMatch(
  source: iTrack,
  candidate: iTrack,
  confidence: number,
  evidence: MatchEvidence,
): boolean {
  if (!durationWithinLimit(source, candidate) || evidence.versionConflict) {
    return false;
  }
  const bothArtistsKnown =
    !isUnknownArtist(source.Artist) && !isUnknownArtist(candidate.Artist);
  if (bothArtistsKnown && evidence.artistScore < 50) {
    return false;
  }
  return confidence >= CLOUD_MATCH_MIN_CONFIDENCE;
}

function tryTitleFallback(
  sourceTrack: iTrack,
  candidates: iTrack[],
): CloudMatchResult | null {
  let bestCandidate: iTrack | undefined;
  let bestSimilarity = 0;

  for (const candidate of candidates) {
    if (
      !durationWithinLimit(sourceTrack, candidate) ||
      !artistsAgree(sourceTrack, candidate)
    ) {
      continue;
    }

    const similarity = titleSimilarity(
      sourceTrack.Title || '',
      candidate.Title || '',
    );
    if (similarity > bestSimilarity) {
      bestSimilarity = similarity;
      bestCandidate = candidate;
    }
  }

  if (bestSimilarity >= 70 && bestCandidate) {
    return {
      found: true,
      matchedTrack: bestCandidate,
      confidence: bestSimilarity,
      requiresReview: false,
    };
  }

  return null;
}

export function findBestCloudMatch(
  sourceTrack: iTrack,
  candidates: iTrack[],
  provider: AssetProvider,
): CloudMatchResult {
  if (!candidates?.length) {
    return {
      found: false,
      matchedTrack: undefined,
      confidence: 0,
      requiresReview: false,
      reason: 'Not found',
    };
  }

  const sourceIsrc = isrcOf(sourceTrack);
  if (sourceIsrc) {
    const isrcHit = candidates.find(
      (candidate) => isrcOf(candidate) === sourceIsrc,
    );
    if (isrcHit) {
      const delta = durationDeltaMs(sourceTrack, isrcHit);
      if (delta == null || delta <= DURATION_HARD_REJECT_MS) {
        return {
          found: true,
          matchedTrack: isrcHit,
          confidence: 100,
          requiresReview: false,
        };
      }
    }
  }

  const sourceMetadata = trackToAssetMetadata(sourceTrack);
  const candidateMetadata = candidates.map((candidate) =>
    trackToAssetMetadata(candidate, candidate.ID),
  );

  const result = TrackMatcher.matchTrack(
    sourceMetadata,
    candidateMetadata,
    provider,
    'remix_sensitive',
  );

  if (!result) {
    const fallback = tryTitleFallback(sourceTrack, candidates);
    if (fallback) {
      return fallback;
    }
    return {
      found: false,
      matchedTrack: undefined,
      confidence: 0,
      requiresReview: false,
      reason: 'Not found',
    };
  }

  // TrackMatcher leaves an artist it can't line up out of the score, so a
  // same-title song by someone else can outrank the right one. Take the best
  // ranked candidate that passes rather than only the top one.
  const ranked = [
    {
      id: result.candidateId,
      confidence: result.confidence,
      evidence: result.evidence,
    },
    ...(result.alternativeCandidates ?? []).map((alternative) => ({
      id: alternative.providerId,
      confidence: alternative.confidence,
      evidence: alternative.evidence,
    })),
  ];

  for (const { id, confidence, evidence } of ranked) {
    const candidate = candidates.find((track) => track.ID === id);
    if (
      candidate &&
      passesCloudMatch(sourceTrack, candidate, confidence, evidence)
    ) {
      return {
        found: true,
        matchedTrack: candidate,
        confidence,
        requiresReview:
          confidence < DEFAULT_MATCHER_CONFIG.thresholds.autoMatchMin,
      };
    }
  }

  const fallback = tryTitleFallback(sourceTrack, candidates);
  if (fallback) {
    return fallback;
  }

  const topCandidate = candidates.find(
    (candidate) => candidate.ID === result.candidateId,
  );
  return {
    found: false,
    matchedTrack: undefined,
    confidence: result.confidence,
    requiresReview: false,
    reason: unmatchedReason(
      topCandidate ? durationDeltaMs(sourceTrack, topCandidate) : undefined,
      result.confidence,
      result.evidence.versionConflict,
    ),
  };
}
